import { useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Logo from '../../../Asset/images/logo-blaxk.png';
import { adminSidebarNavigationItems } from '@/service/buildRoutes';

const DefaultNavIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="12" cy="12" r="10"></circle>
		<line x1="12" y1="16" x2="12" y2="12"></line>
		<line x1="12" y1="8" x2="12.01" y2="8"></line>
	</svg>
);

const ChevronDownIcon = props => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
		<path
			fillRule="evenodd"
			d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.25a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z"
			clipRule="evenodd"
		/>
	</svg>
);

const ChevronUpIcon = props => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
		<path
			fillRule="evenodd"
			d="M14.77 12.79a.75.75 0 0 1-1.06-.02L10 9.06l-3.71 3.71a.75.75 0 1 1-1.06-1.06l4.24-4.25a.75.75 0 0 1 1.06 0l4.24 4.25a.75.75 0 0 1-.02 1.08z"
			clipRule="evenodd"
		/>
	</svg>
);

const SubItemBulletIcon = props => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
		<circle cx="12" cy="12" r="3" />
	</svg>
);

export default function SideBar() {
	const location = useLocation();
	// Tracks explicit user toggles per group. If a group has never been toggled,
	// it will default to open when one of its children is active.
	const [groupOpenOverrides, setGroupOpenOverrides] = useState({});

	const normalizedPathname = useMemo(() => location.pathname.toLowerCase(), [location.pathname]);

	const isPathActive = path => {
		if (!path) return false;
		return normalizedPathname === path.toLowerCase();
	};

	const isGroupActive = item => {
		return Array.isArray(item?.subItems) && item.subItems.some(subItem => isPathActive(subItem.path));
	};

	const isGroupOpen = (item, groupKey) => {
		const override = groupOpenOverrides[groupKey];
		return typeof override === 'boolean' ? override : isGroupActive(item);
	};

	const toggleGroup = (item, groupKey) => {
		setGroupOpenOverrides(prev => {
			const current = typeof prev[groupKey] === 'boolean' ? prev[groupKey] : isGroupActive(item);
			return { ...prev, [groupKey]: !current };
		});
	};

	return (
		<div
			className="main-menu menu-fixed menu-accordion menu-shadow expanded menu-light"
			style={{ width: 260, minWidth: 260, maxWidth: 260, overflowX: 'hidden' }}
		>
			<div className="navbar-header">
				<ul className="nav navbar-nav flex-row">
					<li className="nav-item me-auto mb-2">
						<Link to="/AdminDashboard" className="navbar-brand m-0">
							<span className="c-brand-logo">
								<img src={Logo} alt="" srcSet="" />
							</span>
						</Link>
					</li>
				</ul>
			</div>
			<ul className="navigation navigation-main">
				{/* Map over navigationItems to generate navigation links */}
				{adminSidebarNavigationItems.map((item, index) => {
					const key = item.key ?? item.path ?? item.text ?? index;

					if (Array.isArray(item.subItems) && item.subItems.length) {
						const groupKey = `group:${key}`;
						const groupIsOpen = isGroupOpen(item, groupKey);
						const groupIsActive = isGroupActive(item);

						return (
							<li
								key={key}
								className={`nav-item has-sub ${groupIsOpen ? 'open' : ''} ${groupIsActive ? 'active sidebar-group-active' : ''}`.trim()}
							>
								<a
									href="#"
									onClick={e => {
										e.preventDefault();
										toggleGroup(item, groupKey);
									}}
									aria-expanded={groupIsOpen}
									style={{ position: 'relative' }}
								>
									<DefaultNavIcon />
									{item.text}
									{groupIsOpen ? (
										<ChevronUpIcon
											aria-hidden="true"
											style={{ position: 'absolute', right: 20, top: 14, width: 16, height: 16, marginRight: 0 }}
										/>
									) : (
										<ChevronDownIcon
											aria-hidden="true"
											style={{ position: 'absolute', right: 20, top: 14, width: 16, height: 16, marginRight: 0 }}
										/>
									)}
								</a>
								{groupIsOpen && (
									<ul className="menu-content">
										{item.subItems.map((subItem, subIndex) => {
											const subKey = subItem.key ?? subItem.path ?? `${groupKey}:${subIndex}`;
											const subIsActive = isPathActive(subItem.path);

											return (
												<li key={subKey} className={`${subIsActive ? 'active' : ''}`.trim()}>
													<NavLink to={subItem.path} className={({ isActive }) => (isActive ? 'active' : '')}>
														<SubItemBulletIcon aria-hidden="true" style={{ opacity: 0.85 }} />
														{subItem.text}
													</NavLink>
												</li>
											);
										})}
									</ul>
								)}
							</li>
						);
					}

					const itemIsActive = isPathActive(item.path);

					return (
						<li key={key} className={`nav-item ${itemIsActive ? 'active' : ''}`.trim()}>
							<NavLink to={item.path} className={({ isActive }) => (isActive ? 'active' : '')}>
								<DefaultNavIcon />
								{item.text}
							</NavLink>
						</li>
					);
				})}
			</ul>
		</div>
	);
}
